/**
 * src/pages/Settings.jsx — studio configuration. Owner only.
 *
 * Four things live here, and they are separate forms rather than one big save
 * button: branding, booking rules, the consent wording, and the artist roster.
 * Saving the consent text should not also re-save the studio slug.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { api, uploadFile } from '../lib/api.js';
import { formatDate } from '../lib/format.js';
import { useApi } from '../lib/useApi.js';
import { Empty, ErrorBanner, Loader, PageHeader } from '../components/ui.jsx';
import { Table, Th, RowHeader } from '../components/ui/index.js';
import ColorPicker from '../components/ColorPicker.jsx';
import ConnectOnboardingForm from '../components/ConnectOnboardingForm.jsx';
import { openNativePicker } from '../lib/picker.js';

/**
 * Every IANA zone name the browser knows, for the timezone picker.
 *
 * `Intl.supportedValuesOf` is missing in a handful of older engines; this is
 * a staff-facing settings page, so falling back to just UTC there (rather
 * than a hand-maintained list) is an acceptable, honest degradation.
 */
const TIMEZONE_OPTIONS =
  typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : ['UTC'];

export default function Settings() {
  const { data, error, loading, reload } = useApi('/api/studio');
  const studio = data?.studio;

  return (
    <>
      <PageHeader title="Settings" subtitle="Branding, booking rules, consent wording and your roster." />
      <Loader loading={loading} error={error}>
        {studio ? (
          <div className="stack-lg">
            <Branding studio={studio} onSaved={reload} />
            <Billing studio={studio} />
            <Payouts studio={studio} onSaved={reload} />
            <BookingRules studio={studio} onSaved={reload} />
            <ConsentText studio={studio} onSaved={reload} />
            <Roster />
          </div>
        ) : null}
      </Loader>
    </>
  );
}

/**
 * Small hook giving every settings form the same save/feedback behaviour.
 *
 * @returns {{ busy: boolean, error: string|null, saved: boolean,
 *   save: (fn: () => Promise<unknown>) => Promise<void> }}
 */
function useSaver() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const save = async (fn) => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await fn();
      setSaved(true);
      // The confirmation is transient; leaving it up makes the next save
      // ambiguous.
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return { busy, error, saved, save };
}

/**
 * Studio name, public URL, accent colour and logo.
 *
 * @param {{ studio: object, onSaved: () => void }} props
 * @returns {JSX.Element}
 */
function Branding({ studio, onSaved }) {
  const { busy, error, saved, save } = useSaver();
  const logoRef = useRef(null);
  const [logoName, setLogoName] = useState('');
  const [form, setForm] = useState({
    name: studio.name,
    slug: studio.slug,
    accentColor: studio.accentColor ?? '#1a1a1a',
  });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    save(async () => {
      const patch = { ...form };
      // Upload the logo first so its URL goes in the same PATCH.
      const file = logoRef.current?.files?.[0];
      if (file) {
        const upload = await uploadFile(file, { assetType: 'logo' });
        patch.logoUrl = upload.publicUrl;
      }
      await api.patch('/api/studio', patch);
      onSaved();
    });
  };

  return (
    <section className="card">
      <h2 style={{ marginBottom: 14 }}>Branding</h2>
      <form className="stack" onSubmit={submit}>
        <ErrorBanner>{error}</ErrorBanner>
        {saved ? <div className="alert alert-success">Saved.</div> : null}

        <div className="row wrap" style={{ alignItems: 'flex-start' }}>
          <div className="grow">
            <label htmlFor="sname">Studio name</label>
            <input id="sname" value={form.name} onChange={set('name')} required />
          </div>
          <div className="grow">
            <label htmlFor="sslug">Booking URL</label>
            <input id="sslug" value={form.slug} onChange={set('slug')} required />
            <p className="muted small" style={{ marginTop: 4 }}>/book/{form.slug}</p>
          </div>
        </div>

        <div className="row wrap" style={{ alignItems: 'flex-start' }}>
          <div style={{ minWidth: 260 }}>
            <ColorPicker
              value={form.accentColor}
              onChange={(hex) => setForm((f) => ({ ...f, accentColor: hex }))}
            />
          </div>
          <div className="grow">
            <label htmlFor="sfrom">Emails sent from</label>
            {/* Read-only: derived from the booking URL by the API, so it
                follows a slug change after saving. */}
            <input id="sfrom" type="email" value={studio.fromEmail ?? ''} readOnly />
            <p className="muted small" style={{ marginTop: 4 }}>
              Set automatically from your booking URL. Clients can email this address
              too — replies land in your Inbox.
            </p>
          </div>
        </div>

        <AccentPreview studioName={form.name} rawHex={form.accentColor} />


        <div>
          <label htmlFor="slogo">Logo</label>
          <div className="row">
            {studio.logoUrl ? (
              <img
                src={studio.logoUrl}
                alt="Studio logo"
                style={{ height: 40, borderRadius: 6 }}
              />
            ) : null}
            <input
              id="slogo"
              ref={logoRef}
              type="file"
              accept="image/*"
              className="ui-visually-hidden"
              onChange={(e) => setLogoName(e.target.files?.[0]?.name ?? '')}
            />
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => logoRef.current?.click()}
            >
              Choose logo
            </button>
            <span className="muted small">{logoName || 'No file chosen'}</span>
          </div>
        </div>

        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save branding'}
        </button>
      </form>
    </section>
  );
}

/**
 * REDESIGN.md's "theme preview" for this phase — not a portal-theme picker
 * (no `studios.portal_theme` column exists to persist that choice against;
 * see theme-migration.md's schema-question resolution, still standing),
 * just a live, accurate preview of the one thing this page actually
 * controls: how the studio's chosen accent colour will really look once
 * lib/studio-accent.js applies it on the booking page a client sees. Reads
 * the *unsaved* form value, live, so it updates as the owner types or picks
 * a swatch — before clicking Save, not after.
 *
 * Mirrors the safe-derivation formula from design-tokens/themes.js's
 * generateCSS() (`color-mix(in oklab, <raw> 50%, black 50%)`) via a CSS
 * custom property scoped to this box (see .settings-accent-preview in
 * styles.css) rather than that file's own `@supports`-guarded fallback
 * chain — color-mix() is Baseline widely available today, and this is a
 * low-stakes preview widget, not a payment or legal flow, so the extra
 * fallback branch wasn't worth duplicating here.
 *
 * @param {{ studioName: string, rawHex: string }} props
 * @returns {JSX.Element}
 */
function AccentPreview({ studioName, rawHex }) {
  const swatchRef = useRef(null);
  const [onAccent, setOnAccent] = useState(null);

  // --on-accent is a fixed per-theme token ('#fff' in Nocturne's dark mode,
  // '#000' in light) — right for the real --accent, which is always
  // white/black, but wrong here, where --accent is overridden to whatever
  // the studio picked. A dark pick in dark mode then paired black text on a
  // near-black swatch. Read back the swatch's *actual rendered* background
  // (post color-mix, via the canvas trick below — the only reliable way to
  // resolve an arbitrary computed CSS colour to sRGB bytes across browsers)
  // and pick whichever of black/white clears a higher contrast ratio against
  // it, same WCAG relative-luminance formula the contrast test elsewhere in
  // this app already holds every other colour pairing to.
  useLayoutEffect(() => {
    const el = swatchRef.current;
    if (!el) return;
    const bg = getComputedStyle(el).backgroundColor;
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');
    // jsdom (the SPA test environment) stubs getContext('2d') as a no-op
    // returning null — a real capability gap, not a scenario to special-case
    // away, so fall back to leaving --on-accent unset (the theme default).
    if (!ctx) return;
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
    const toLinear = (c) => {
      const s = c / 255;
      return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    };
    const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
    const contrastWithWhite = 1.05 / (luminance + 0.05);
    const contrastWithBlack = (luminance + 0.05) / 0.05;
    setOnAccent(contrastWithWhite >= contrastWithBlack ? '#ffffff' : '#000000');
  }, [rawHex]);

  return (
    <div>
      <p className="color-picker-label" style={{ marginBottom: 8 }}>
        Preview — how this looks on your booking page
      </p>
      <div
        className="settings-accent-preview"
        style={{
          '--preview-accent-raw': rawHex,
          ...(onAccent ? { '--on-accent': onAccent } : {}),
        }}
      >
        <span ref={swatchRef} className="settings-accent-preview-swatch" aria-hidden="true" />
        <div className="grow">
          <div style={{ fontWeight: 700, color: 'var(--accent)' }}>
            {studioName || 'Your studio'}
          </div>
          <button type="button" className="btn btn-primary btn-sm" tabIndex={-1}>
            Set up your studio
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The studio's own subscription to the platform — a Stripe Customer/
 * Subscription on the platform's Stripe account, distinct from the Payouts
 * panel below it (that's the studio's own Connect account, for receiving
 * deposits). This panel only ever launches Stripe's hosted Billing Portal;
 * there is no cancel/update-card UI of our own.
 *
 * A locked-out studio never actually reaches this page — App.jsx's
 * RequireSubscription shows a "Subscribe to continue" screen first — so
 * this panel is really just the self-serve "manage/cancel" surface for an
 * already-active (or past_due/canceled-but-still-signed-in) subscription.
 *
 * @param {{ studio: object }} props
 * @returns {JSX.Element}
 */
function Billing({ studio }) {
  const { busy, error, save } = useSaver();

  const manageBilling = () =>
    save(async () => {
      const res = await api.post('/api/studio/billing-portal');
      window.location.assign(res.url);
    });

  // Active but cancelled is still "active" to Stripe until the paid period
  // runs out — only a subscription that will actually renew gets the green.
  const renewing = studio.subscriptionStatus === 'active' && !studio.subscriptionCancelAt;

  return (
    <section className="card">
      <h2 style={{ marginBottom: 6 }}>Billing</h2>
      <p className="muted small" style={{ marginBottom: 14 }}>
        Your studio&rsquo;s own subscription to this platform.
      </p>
      <ErrorBanner>{error}</ErrorBanner>
      <div className="stack">
        <div className={`alert${renewing ? ' alert-success' : ''}`}>
          {billingStatusLabel(studio.subscriptionStatus, studio.subscriptionCancelAt)}
        </div>
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy}
          onClick={manageBilling}
          style={{ alignSelf: 'flex-start' }}
        >
          {busy ? 'Redirecting…' : 'Manage billing'}
        </button>
      </div>
    </section>
  );
}

/**
 * @param {string} status subscriptionStatus from publicStudio().
 * @param {number|null} [cancelAt] subscriptionCancelAt from publicStudio():
 *   when a cancelled subscription's paid period ends, null if it renews.
 * @returns {string}
 */
export function billingStatusLabel(status, cancelAt) {
  switch (status) {
    case 'active':
      return cancelAt
        ? `Your subscription has been cancelled and won’t renew. You still have full access until ${formatDate(cancelAt)} — resume it from Manage billing to keep your studio running after that.`
        : 'Your subscription is active.';
    case 'past_due':
      return 'Your last payment failed — update your card to avoid losing access.';
    case 'canceled':
      return 'Your subscription is cancelled.';
    default:
      return 'Subscription not yet started.';
  }
}

/**
 * Stripe Connect Express payout status and onboarding trigger.
 *
 * Reflects status back via the connectStarted/payoutsEnabled/chargesEnabled
 * fields on the studio (never the raw Stripe account id). No card, bank or
 * identity field is collected here or anywhere else in this app.
 *
 * Onboarding itself happens one of two ways, decided by whether the studio
 * row carries a `stripePublishableKey` (canUseEmbeddedPayments() on the API
 * — false under the mock transport or with no publishable key configured):
 *  - embeddable: clicking the button mounts ConnectOnboardingForm in-page.
 *  - otherwise: clicking the button redirects to Stripe's hosted page,
 *    same as the deposit/subscription forms' own embedded/hosted split.
 *
 * @param {{ studio: object, onSaved: () => void }} props
 * @returns {JSX.Element}
 */
function Payouts({ studio, onSaved }) {
  const { busy, error, save } = useSaver();
  const [params, setParams] = useSearchParams();
  const connect = params.get('connect');
  const [showEmbedded, setShowEmbedded] = useState(false);
  const embeddable = Boolean(studio.stripePublishableKey);

  const startOnboarding = () =>
    save(async () => {
      const res = await api.post('/api/studio/connect/onboard');
      window.location.assign(res.url);
    });

  // Handles the two round trips Stripe's hosted onboarding sends the browser
  // back with: `return` (finished the steps — status may still read pending,
  // Stripe's own verification can outlast the redirect) re-fetches the
  // studio; `refresh` (the Account Link expired mid-flow) transparently
  // mints a fresh one and redirects straight back out. Both only ever fire
  // from the hosted-redirect path — the embedded form never navigates away.
  useEffect(() => {
    if (connect === 'return') {
      onSaved();
      setParams((p) => {
        p.delete('connect');
        return p;
      }, { replace: true });
    } else if (connect === 'refresh') {
      startOnboarding();
    }
    // Only re-run when the query param itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connect]);

  const status = studio.payoutsEnabled ? 'active' : studio.connectStarted ? 'pending' : 'not_connected';

  return (
    <section className="card">
      <h2 style={{ marginBottom: 6 }}>Payouts</h2>
      <p className="muted small" style={{ marginBottom: 14 }}>
        Connect your bank account so deposits are paid straight to you. Stripe hosts the setup —
        we never see your card, bank or identity details.
      </p>
      <div className="stack">
        <ErrorBanner>{error}</ErrorBanner>

        {status === 'active' ? (
          <div className="alert alert-success">Deposits are paid directly to your bank account.</div>
        ) : showEmbedded && embeddable ? (
          <ConnectOnboardingForm
            publishableKey={studio.stripePublishableKey}
            onExit={() => {
              setShowEmbedded(false);
              onSaved();
            }}
          />
        ) : (
          <>
            <div className="alert">
              {status === 'pending'
                ? 'Setup started — Stripe is still verifying your details. This can take a little while.'
                : 'Not connected yet — deposits have nowhere to go until you connect a bank account.'}
            </div>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={embeddable ? () => setShowEmbedded(true) : startOnboarding}
              style={{ alignSelf: 'flex-start' }}
            >
              {busy ? 'Redirecting…' : status === 'pending' ? 'Finish setup' : 'Connect your bank account'}
            </button>
          </>
        )}
      </div>
    </section>
  );
}

/**
 * Working hours, slot granularity, session length, deposit and currency.
 *
 * @param {{ studio: object, onSaved: () => void }} props
 * @returns {JSX.Element}
 */
function BookingRules({ studio, onSaved }) {
  const { busy, error, saved, save } = useSaver();
  const s = studio.settings;
  const [form, setForm] = useState({
    start: s.workingHours.start,
    end: s.workingHours.end,
    slotMinutes: s.slotMinutes,
    defaultSessionMinutes: s.defaultSessionMinutes,
    timezone: s.timezone,
    // Shown in whole currency units; stored in minor units.
    deposit: (s.defaultDepositAmount / 100).toFixed(2),
    currency: s.currency,
    paymentUi: s.paymentUi,
  });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    save(async () => {
      await api.patch('/api/studio', {
        settings: {
          workingHours: { start: form.start, end: form.end },
          slotMinutes: Number(form.slotMinutes),
          defaultSessionMinutes: Number(form.defaultSessionMinutes),
          timezone: form.timezone,
          defaultDepositAmount: Math.round(Number(form.deposit) * 100),
          currency: form.currency,
          paymentUi: form.paymentUi,
        },
      });
      onSaved();
    });
  };

  return (
    <section className="card">
      <h2 style={{ marginBottom: 6 }}>Public booking</h2>
      <p className="muted small" style={{ marginBottom: 14 }}>
        These drive the slots clients see at /book/{studio.slug}.
      </p>
      <form className="stack" onSubmit={submit}>
        <ErrorBanner>{error}</ErrorBanner>
        {saved ? <div className="alert alert-success">Saved.</div> : null}

        <div className="row wrap">
          <div style={{ width: 130 }}>
            <label htmlFor="bstart">Open</label>
            <input id="bstart" type="time" value={form.start} onChange={set('start')} onClick={openNativePicker} />
          </div>
          <div style={{ width: 130 }}>
            <label htmlFor="bend">Close</label>
            <input id="bend" type="time" value={form.end} onChange={set('end')} onClick={openNativePicker} />
          </div>
          <div style={{ width: 130 }}>
            <label htmlFor="bslot">Slot (mins)</label>
            <input
              id="bslot"
              type="number"
              min="15"
              step="15"
              value={form.slotMinutes}
              onChange={set('slotMinutes')}
            />
          </div>
          <div style={{ width: 150 }}>
            <label htmlFor="bsession">Session (mins)</label>
            <input
              id="bsession"
              type="number"
              min="30"
              step="30"
              value={form.defaultSessionMinutes}
              onChange={set('defaultSessionMinutes')}
            />
          </div>
          <div className="grow" style={{ minWidth: 220 }}>
            <label htmlFor="btimezone">Timezone</label>
            <select id="btimezone" value={form.timezone} onChange={set('timezone')}>
              {/* Guarantees the studio's current value is selectable even if
                  it is somehow missing from the browser's own list. */}
              {(TIMEZONE_OPTIONS.includes(form.timezone)
                ? TIMEZONE_OPTIONS
                : [form.timezone, ...TIMEZONE_OPTIONS]
              ).map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
            <p className="muted small" style={{ marginTop: 4 }}>
              Open and close above are in this zone.
            </p>
          </div>
        </div>

        <div className="row wrap">
          <div style={{ width: 150 }}>
            <label htmlFor="bdeposit">Deposit</label>
            <input
              id="bdeposit"
              type="number"
              min="0"
              step="0.01"
              value={form.deposit}
              onChange={set('deposit')}
            />
          </div>
          <div style={{ width: 130 }}>
            <label htmlFor="bcurrency">Currency</label>
            <select id="bcurrency" value={form.currency} onChange={set('currency')}>
              <option value="gbp">GBP</option>
              <option value="eur">EUR</option>
              <option value="usd">USD</option>
              <option value="aud">AUD</option>
              <option value="cad">CAD</option>
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="bpaymentui">Where clients pay their deposit</label>
          <select id="bpaymentui" value={form.paymentUi} onChange={set('paymentUi')}>
            <option value="embedded">On your booking page (recommended)</option>
            <option value="redirect">On Stripe&rsquo;s own checkout page</option>
          </select>
          <p className="muted small" style={{ marginTop: 4 }}>
            Either way Stripe processes the card and no card number reaches this app. Embedded
            keeps clients inside your branded flow; Stripe&rsquo;s page handles declines and
            3-D Secure prompts for you. Embedded needs a Stripe publishable key configured — if
            none is set, the booking page falls back to Stripe&rsquo;s page automatically.
          </p>
        </div>

        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save booking rules'}
        </button>
      </form>
    </section>
  );
}

/**
 * The consent wording clients are shown and sign.
 *
 * @param {{ studio: object, onSaved: () => void }} props
 * @returns {JSX.Element}
 */
function ConsentText({ studio, onSaved }) {
  const { busy, error, saved, save } = useSaver();
  const [text, setText] = useState(studio.settings.consentFormText);

  return (
    <section className="card">
      <h2 style={{ marginBottom: 6 }}>Consent form wording</h2>
      <p className="muted small" style={{ marginBottom: 14 }}>
        This is what clients read and sign, and what goes into the archived PDF. The default covers
        age, health disclosure, aftercare and photo release — have it reviewed for your jurisdiction.
      </p>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save(async () => {
            await api.patch('/api/studio', { settings: { consentFormText: text } });
            onSaved();
          });
        }}
      >
        <ErrorBanner>{error}</ErrorBanner>
        {saved ? <div className="alert alert-success">Saved.</div> : null}
        <div>
          {/* Visible label, not just aria-label: this is the actual legal
              wording a client signs, worth a real, always-visible <label>
              like every other field on this page, not a placeholder or a
              screen-reader-only string. Missing entirely before — caught
              by axe against real seeded content, not a token-level check. */}
          <label htmlFor="consentText">Wording</label>
          <textarea
            id="consentText"
            value={text}
            onChange={(e) => setText(e.target.value)}
            style={{ minHeight: 320, fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }}
          />
        </div>
        <p className="muted small">
          Changing this affects forms signed from now on. Already-signed PDFs keep the wording that
          was in force when they were signed.
        </p>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save wording'}
        </button>
      </form>
    </section>
  );
}

/**
 * Artist roster management.
 *
 * @returns {JSX.Element}
 */
function Roster() {
  const { data, error, loading, reload } = useApi('/api/artists?includeInactive=1');
  const { busy, error: actionError, save } = useSaver();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'artist' });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const artists = data?.artists ?? [];

  return (
    <section className="card">
      <div className="row-between" style={{ marginBottom: 14 }}>
        <h2>Artists</h2>
        <button type="button" className="btn btn-sm" onClick={() => setAdding((a) => !a)}>
          {adding ? 'Cancel' : 'Add artist'}
        </button>
      </div>

      <ErrorBanner>{actionError}</ErrorBanner>

      {adding ? (
        <form
          className="stack card"
          style={{ marginBottom: 16, background: 'var(--bg)' }}
          onSubmit={(e) => {
            e.preventDefault();
            save(async () => {
              await api.post('/api/artists', form);
              setForm({ name: '', email: '', role: 'artist' });
              setAdding(false);
              reload();
            });
          }}
        >
          <div className="row wrap">
            <div className="grow">
              <label htmlFor="aname">Name</label>
              <input id="aname" value={form.name} onChange={set('name')} required />
            </div>
            <div className="grow">
              <label htmlFor="aemail">Email</label>
              <input id="aemail" type="email" value={form.email} onChange={set('email')} required />
            </div>
            <div style={{ width: 140 }}>
              <label htmlFor="arole">Role</label>
              <select id="arole" value={form.role} onChange={set('role')}>
                <option value="artist">Artist</option>
                <option value="owner">Owner</option>
              </select>
            </div>
          </div>
          <p className="muted small">
            They'll get an email to set their own password and join.
          </p>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            Send invite
          </button>
        </form>
      ) : null}

      <Loader loading={loading} error={error}>
        {artists.length === 0 ? (
          <Empty>No artists yet.</Empty>
        ) : (
          <Table caption="Artists">
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <th scope="col"><span className="ui-visually-hidden">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {/* No opacity-dimming for a deactivated row: it uniformly
                  crushed every cell's contrast below 4.5:1 (caught by axe
                  against real seeded data, not a token-level check — see
                  progress.md), and it was redundant anyway — the Status
                  column already says "Deactivated" in plain text, which is
                  the whole point of REDESIGN.md's "status is never
                  colour-only" rule applied one step further. */}
              {artists.map((a) => (
                <tr key={a.id}>
                  <RowHeader>{a.name}</RowHeader>
                  <td className="muted">{a.email}</td>
                  <td className="muted">{a.role}</td>
                  <td className="muted">
                    {a.isActive ? 'Active' : a.inviteStatus === 'invited' ? 'Invited' : 'Deactivated'}
                  </td>
                  <td>
                      {a.isActive ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          disabled={busy}
                          onClick={() =>
                            save(async () => {
                              await api.del(`/api/artists/${a.id}`);
                              reload();
                            })
                          }
                        >
                          Deactivate
                        </button>
                      ) : a.inviteStatus === 'invited' ? (
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={busy}
                          onClick={() =>
                            save(async () => {
                              await api.post(`/api/artists/${a.id}/invite/resend`);
                              reload();
                            })
                          }
                        >
                          Resend invite
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={busy}
                          onClick={() =>
                            save(async () => {
                              await api.patch(`/api/artists/${a.id}`, { isActive: true });
                              reload();
                            })
                          }
                        >
                          Reactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
        )}
      </Loader>
    </section>
  );
}
